/**
 * The room's areas, and where everybody stands in them.
 *
 * One room, three areas side by side, each the width of the Bug Hunt's floor:
 *
 *   Bugs      — people whose agent is on a bug, with the jar of waiting bugs.
 *   Features  — people whose agent is on a feature, a task, an SDD or a PRD.
 *   Others    — exploration, analysis, anything undeclared; and people with no agent running.
 *
 * A person is in the area of what their agent is doing now, so the room rearranges itself as work
 * changes. Where inside an area is computed, unless the viewer dragged them somewhere, in which case
 * that spot is kept — per area, so dragging someone in Bugs does not decide where they stand when
 * they move on to a feature.
 */

import { computeFloor, containmentBox, horizonFor, type FloorSize, type Station } from '../bughunt/layout'
import type { ActivitySession, DeveloperActivity } from '../lib/types'

export type AreaId = 'bugs' | 'features' | 'others'

export const AREAS: AreaId[] = ['bugs', 'features', 'others']

/** Each area is one Bug Hunt floor wide. */
export const AREA_WIDTH = 1000

export const ROOM_WIDTH = AREA_WIDTH * AREAS.length

export function areaX(area: AreaId): number {
  return AREAS.indexOf(area) * AREA_WIDTH
}

/**
 * The session that decides where somebody stands: the most recently active one with an agent
 * reporting, or else the most recent at all.
 */
export function primarySession(activity: DeveloperActivity | undefined): ActivitySession | undefined {
  if (!activity || activity.sessions.length === 0) return undefined
  // Stable while several agents work (bug SALA-PISCA-1): the sessions come newest-activity first, so "the first
  // one working" changed with every event — the main agent and the second swapped places, and a person whose two
  // agents were on different kinds of work jumped between areas. The one that started first stays the main one.
  const working = activity.sessions.filter((s) => s.activityState !== 'Idle')
  if (working.length > 0) return working.reduce((first, s) => (s.startedAt < first.startedAt || (s.startedAt === first.startedAt && s.workSessionId < first.workSessionId) ? s : first))
  return activity.sessions[0]
}

/** Which area a person belongs in, from what their agent is doing now. */
export function areaOf(activity: DeveloperActivity | undefined): AreaId {
  const session = primarySession(activity)
  if (!session) return 'others'

  switch (session.category) {
    case 'Bug':
      return 'bugs'
    case 'Spec':
    case 'Task':
      return 'features'
    default:
      return 'others'
  }
}

export interface Placed extends Station {
  area: AreaId
}

/** A spot a viewer dragged somebody to, in room coordinates, keyed `${developerId}:${area}`. */
export type Overrides = Record<string, { x: number; y: number }>

export function overrideKey(developerId: string, area: AreaId): string {
  return `${developerId}:${area}`
}

/** The strip of floor each area's stations use: clear of the walls, and of the jar in Bugs. */
function boundsFor(area: AreaId, size: FloorSize): { left: number; right: number } {
  if (area === 'bugs') return { left: 70, right: containmentBox(size).x - 40 }
  return { left: 70, right: AREA_WIDTH - 70 }
}

/**
 * Keeps a dragged station on its own area's floor: inside the area's edges, below the wall and
 * above the bottom of the room. A person cannot be dropped into another area by dragging — the
 * area says what they are doing, and that is not the viewer's to change.
 */
export function clampToArea(area: AreaId, x: number, y: number, size: FloorSize): { x: number; y: number } {
  const left = areaX(area) + 50
  const right = areaX(area) + AREA_WIDTH - 50
  const top = horizonFor(size) + 60
  const bottom = size.height - 20

  return { x: Math.min(right, Math.max(left, x)), y: Math.min(bottom, Math.max(top, y)) }
}

/**
 * Places everybody: grouped by area, laid out within it, then moved to wherever the viewer dragged
 * them. Stable for a given input, so a re-render does not shuffle the room.
 */
export function layoutRoom(
  developers: Array<{ id: string; area: AreaId }>,
  size: FloorSize,
  overrides: Overrides = {},
): Map<string, Placed> {
  const placed = new Map<string, Placed>()

  for (const area of AREAS) {
    const ids = developers.filter((d) => d.area === area).map((d) => d.id)
    if (ids.length === 0) continue

    const floor = computeFloor(ids, size, boundsFor(area, size))
    const offset = areaX(area)

    for (const station of floor.stations) {
      const saved = overrides[overrideKey(station.developerId, area)]
      const spot = saved ? clampToArea(area, saved.x, saved.y, size) : { x: station.x + offset, y: station.y }
      placed.set(station.developerId, { ...station, ...spot, area })
    }
  }

  return placed
}
